import moment from 'moment';
import { ESTADOS_EXPEDIENTE } from '@/helpers/expedienteEstados';

const CLAVES_CONOCIDAS = [
  'fechaInicio', 'fechaFin', 'buscarTexto', 'contratista', 'responsable', 'estatus', 'estadoExpediente',
];
const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;
const VALORES_ESTADO = ESTADOS_EXPEDIENTE.map(({ valor }) => valor);

const esFechaValida = (fecha) => typeof fecha === 'string'
  && FORMATO_FECHA.test(fecha)
  && moment(fecha, 'YYYY-MM-DD', true).isValid();

/**
 * Construye los filtros iniciales a partir de la query string, aceptando unicamente
 * claves conocidas y descartando valores invalidos (se conserva el default en ese caso).
 * @param {Object} query - this.$route.query
 * @param {Object} filtrosDefault
 * @returns {Object} filtros saneados
 */
const sanitizarFiltrosDesdeQuery = (query, filtrosDefault) => {
  const filtros = { ...filtrosDefault };
  if (!query) return filtros;

  CLAVES_CONOCIDAS.forEach((clave) => {
    if (!(clave in query)) return;
    const valor = query[clave];

    if (clave === 'fechaInicio' || clave === 'fechaFin') {
      if (esFechaValida(valor)) filtros[clave] = valor;
      return;
    }
    if (clave === 'estadoExpediente') {
      if (VALORES_ESTADO.includes(valor)) filtros[clave] = valor;
      return;
    }
    if (clave === 'buscarTexto') {
      filtros[clave] = typeof valor === 'string' ? valor : '';
      return;
    }
    filtros[clave] = typeof valor === 'string' && valor ? valor : null;
  });

  return filtros;
};

/**
 * Elimina del objeto de filtros los valores vacios/nulos, para no ensuciar la URL.
 * @param {Object} filtros
 * @returns {Object}
 */
const limpiarFiltrosParaQuery = (filtros) => Object.fromEntries(
  Object.entries(filtros).filter(([, valor]) => !!valor),
);

/**
 * Limpia contratista/responsable/estatus cuando el id ya no existe en el dataset cargado
 * (id inventado en la URL, o que ya no aparece en el rango de fechas actual).
 * @param {Object} filtros
 * @param {Array} datosCompletos
 * @returns {Object} filtros saneados
 */
const sanearFiltrosContraDatos = (filtros, datosCompletos) => {
  const idsContratista = new Set(
    datosCompletos.map(({ contratista }) => contratista && contratista.id).filter(Boolean),
  );
  const idsEstatus = new Set(
    datosCompletos.map(({ estatus }) => estatus && estatus.id).filter(Boolean),
  );
  const idsResponsable = new Set(
    datosCompletos
      .flatMap(({ responsables }) => responsables)
      .filter(({ principal }) => principal)
      .map(({ funcionario }) => funcionario.id),
  );

  return {
    ...filtros,
    contratista: filtros.contratista && idsContratista.has(filtros.contratista)
      ? filtros.contratista : null,
    estatus: filtros.estatus && idsEstatus.has(filtros.estatus)
      ? filtros.estatus : null,
    responsable: filtros.responsable && idsResponsable.has(filtros.responsable)
      ? filtros.responsable : null,
  };
};

export { sanitizarFiltrosDesdeQuery, limpiarFiltrosParaQuery, sanearFiltrosContraDatos };
